import { CheckCircleFilled } from '@ant-design/icons';
import { type EsitoValidazioneRichiestaDTO } from '@framework/api-client/api/inquiries-compile/schemas/esitoValidazioneRichiestaDTO.ts';
import { type RichiestaModuloDTO } from '@framework/api-client/api/inquiries-compile/schemas/richiestaModuloDTO.ts';
import { AlertMessage } from '@framework/form/form/errors/message/AlertMessage.tsx';
import { getModuleTitle } from '@framework/frontoffice-submission/module/getModuleTitle.ts';
import { generatePath, Link } from '@framework/ui/core/router/TypedRoute.tsx';
import { Alert } from 'antd';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

export interface ISubmissionValidationAlertProps {
  submissionId: string;
  result: EsitoValidazioneRichiestaDTO;
  modules?: RichiestaModuloDTO[];
}

/**
 * Componente che mostra un messaggio di alert in base al risultato della validazione della richiesta.
 */
export function SubmissionValidationAlert(
  props: ISubmissionValidationAlertProps,
) {
  const { submissionId, result, modules } = props;
  const { t } = useTranslation();

  const errorMessages = useMemo(() => {
    if (!result.errors?.length) return undefined;
    return result.errors
      .map((err, index) => {
        const errorText =
          err.error ??
          t('form.validazione-errore-sconosciuto', 'Errore sconosciuto');
        const module = modules?.find(m => m.id === err.idRichiestaModulo);
        const moduleName = module ? getModuleTitle(module) : undefined;

        if (moduleName && module?.id) {
          const moduleLink = generatePath('/richieste/:id/moduli/:moduleId', {
            id: submissionId,
            moduleId: module.id,
          });
          return (
            <span key={index}>
              {errorText} — <Link to={moduleLink}>{moduleName}</Link>
            </span>
          );
        }
        return errorText;
      })
      .filter(Boolean);
  }, [result.errors, modules, submissionId, t]);

  if (result.invioAbilitato) {
    return (
      <Alert
        type="success"
        showIcon
        icon={<CheckCircleFilled />}
        closable={false}
        message={t(
          'form.validazione-successo-titolo',
          'Validazione Istanza completata con successo',
        )}
        style={{ marginBottom: 16 }}
      />
    );
  }

  return (
    <AlertMessage
      closable={false}
      errors={errorMessages}
      messageText={t(
        'form.alert-message.summary.content',
        "Sono presenti errori nella Verifica Formale dell'invio Richiesta ({{errors_count}})",
        { errors_count: errorMessages?.length ?? 0 },
      )}
    />
  );
}
